/*
  <vr-carousel> — a dependency-free, scroll-snap carousel used by the homepage collection
  slideshow, the "In the spotlight" product carousel and the footer press-logo ticker.
  It replaces jQuery + slick (≈130 KB of JavaScript and two stylesheets).

  Markup contract:
    <vr-carousel data-autoplay="2000" data-loop="true" data-marquee="false">
      <div class="vr-carousel__track"> <div class="vr-carousel__slide">…</div> … </div>
      <button class="vr-carousel__arrow vr-carousel__arrow--prev">…</button>   (optional)
      <button class="vr-carousel__arrow vr-carousel__arrow--next">…</button>   (optional)
      <div class="vr-carousel__dots"></div>                                    (optional, filled by JS)
    </vr-carousel>

  Slides per view are controlled purely by CSS (the --vr-per-view custom property), so
  there is no layout work in JavaScript and no reflow when images finish loading.
*/
(function () {
  if (customElements.get('vr-carousel')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- deferred images (data-vr-src / data-vr-srcset) ----------
     Below-the-fold sections render their images without src (snippets/defer-image.liquid) so nothing
     competes with the hero image for bandwidth. They are hydrated once the hero has loaded, or at
     window load, or after 4 s at the latest; the theme editor re-hydrates sections it re-renders. */
  function hydrateDeferred(root) {
    (root || document).querySelectorAll('img[data-vr-src], img[data-vr-srcset], source[data-vr-srcset]').forEach(el => {
      if (el.dataset.vrSrcset) { el.setAttribute('srcset', el.dataset.vrSrcset); el.removeAttribute('data-vr-srcset'); }
      if (el.dataset.vrSrc) { el.setAttribute('src', el.dataset.vrSrc); el.removeAttribute('data-vr-src'); }
    });
  }
  (function scheduleHydration() {
    let done = false;
    const go = () => { if (done) return; done = true; hydrateDeferred(); };
    // The hero is the high-priority image inside <main>; the header logo is also marked high and sits before
    // <main>, and keying on it would release everything while the hero is still downloading.
    const hero = document.querySelector('#MainContent img[fetchpriority="high"], main img[fetchpriority="high"]') || document.querySelector('img[fetchpriority="high"]');
    if (document.readyState === 'complete' || (hero && hero.complete)) {
      setTimeout(go, 150);
    } else {
      window.addEventListener('load', go, { once: true });
      if (hero) hero.addEventListener('load', () => setTimeout(go, 150), { once: true });
      setTimeout(go, 4000);
    }
    document.addEventListener('shopify:section:load', e => hydrateDeferred(e.target));
  })();

  /* Second (hover) images on product cards carry data-vr-hover-src / -srcset and download only when a
     pointer or keyboard focus reaches the card, so phones never pay for them. */
  function hydrateHover(card) {
    /* <source> first, so the <img> picks its file with the sources already in place. The photo cross-fades
       only once the hover picture has arrived (.vr-hover-ready on the media box), never to an empty frame. */
    card.querySelectorAll('source[data-vr-hover-srcset], img[data-vr-hover-src], img[data-vr-hover-srcset]').forEach(el => {
      if (el.tagName === 'IMG') {
        const box = el.closest('.media');
        const ready = () => { if (box) box.classList.add('vr-hover-ready'); };
        el.addEventListener('load', ready, { once: true });
        if (el.dataset.vrHoverSrcset) { el.setAttribute('srcset', el.dataset.vrHoverSrcset); el.removeAttribute('data-vr-hover-srcset'); }
        if (el.dataset.vrHoverSrc) { el.setAttribute('src', el.dataset.vrHoverSrc); el.removeAttribute('data-vr-hover-src'); }
        if (el.complete && el.naturalWidth > 0) ready();
      } else if (el.dataset.vrHoverSrcset) {
        el.setAttribute('srcset', el.dataset.vrHoverSrcset); el.removeAttribute('data-vr-hover-srcset');
      }
    });
  }
  ['pointerover', 'focusin'].forEach(ev => document.addEventListener(ev, e => {
    const card = e.target && e.target.closest ? e.target.closest('.card-wrapper') : null;
    if (card) hydrateHover(card);
  }));

  class VrCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.vr-carousel__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (this.slides.length < 2) { this.classList.add('vr-carousel--static'); return; }

      this.prevBtn = this.querySelector('.vr-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.vr-carousel__arrow--next');
      this.dotsWrap = this.querySelector('.vr-carousel__dots');
      this.autoplayMs = parseInt(this.dataset.autoplay || '0', 10);
      this.loop = this.dataset.loop !== 'false';
      this.marquee = this.dataset.marquee === 'true';
      this.pageIndex = 0;
      this.timer = null;
      this.paused = false;

      this.track.setAttribute('role', 'list');
      this.slides.forEach((s, i) => { s.setAttribute('role', 'listitem'); s.dataset.index = String(i); });

      if (this.marquee) { this.setupMarquee(); return; }

      this.onScroll = this.debounce(() => this.syncFromScroll(), 80);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.stop(); this.go(-1); this.restart(); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.stop(); this.go(1); this.restart(); });

      this.buildDots();
      this.updateControls();

      // Pause when the user interacts, when the tab is hidden, or when the carousel is off-screen.
      ['pointerenter', 'focusin', 'touchstart'].forEach(ev => this.addEventListener(ev, () => this.pause(), { passive: true }));
      ['pointerleave', 'focusout', 'touchend'].forEach(ev => this.addEventListener(ev, () => this.resume(), { passive: true }));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) this.resume(); else this.pause();
        }, { threshold: 0.25 });
        this.io.observe(this);
      } else { this.visible = true; }

      window.addEventListener('resize', this.debounce(() => { this.buildDots(); this.syncFromScroll(); }, 150));
      // If stylesheets or fonts are still arriving, slide widths can change after the first layout: re-measure on load.
      if (document.readyState !== 'complete') window.addEventListener('load', () => { this.buildDots(); this.syncFromScroll(); }, { once: true });
      this.restart();
    }

    disconnectedCallback() { this.stop(); if (this.io) this.io.disconnect(); }

    /* ---------- geometry helpers ---------- */
    slideWidth() { const s = this.slides[0]; const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '0') || 0; return s.getBoundingClientRect().width + gap; }
    perView() { const w = this.slideWidth(); return w ? Math.max(1, Math.round((this.track.clientWidth + 1) / w)) : 1; }
    pageCount() { return Math.max(1, Math.ceil(this.slides.length / this.perView())); }

    scrollToPage(page, smooth) {
      const pages = this.pageCount();
      if (this.loop) page = (page + pages) % pages; else page = Math.min(Math.max(page, 0), pages - 1);
      this.pageIndex = page;
      const target = Math.min(page * this.perView() * this.slideWidth(), this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollTo({ left: target, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
      this.updateControls();
    }

    go(delta) { this.scrollToPage(this.pageIndex + delta, true); }

    syncFromScroll() {
      const w = this.slideWidth() * this.perView();
      if (!w) return;
      const page = Math.round(this.track.scrollLeft / w);
      if (page !== this.pageIndex) { this.pageIndex = Math.min(page, this.pageCount() - 1); this.updateControls(); }
    }

    /* ---------- dots + arrows ---------- */
    buildDots() {
      if (!this.dotsWrap) return;
      const pages = this.pageCount();
      if (this.dotsWrap.childElementCount === pages) return;
      this.dotsWrap.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'vr-carousel__dot';
        b.setAttribute('aria-label', (this.dataset.dotLabel || 'Go to slide') + ' ' + (i + 1));
        b.addEventListener('click', () => { this.stop(); this.scrollToPage(i, true); this.restart(); });
        this.dotsWrap.appendChild(b);
      }
      this.updateControls();
    }

    updateControls() {
      if (this.dotsWrap) Array.from(this.dotsWrap.children).forEach((d, i) => { const on = i === this.pageIndex; d.classList.toggle('is-active', on); d.setAttribute('aria-current', on ? 'true' : 'false'); });
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = this.pageIndex === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.pageIndex >= this.pageCount() - 1;
      }
    }

    /* ---------- autoplay ---------- */
    restart() { this.stop(); if (this.autoplayMs > 0 && !reducedMotion.matches) this.timer = setInterval(() => { if (!this.paused && this.visible !== false) this.go(1); }, this.autoplayMs); }
    stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
    pause() { this.paused = true; }
    resume() { this.paused = false; }

    /* ---------- marquee mode (continuous ticker, CSS-animated) ---------- */
    setupMarquee() {
      if (reducedMotion.matches) return;
      // Duplicate the slides once so the CSS animation can loop seamlessly.
      const clone = this.track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
      this.appendChild(clone);
      this.classList.add('vr-carousel--marquee');
      const seconds = parseFloat(this.dataset.marqueeSeconds || '30');
      this.style.setProperty('--vr-marquee-duration', seconds + 's');
      // Loop over a measured whole-pixel distance: a percentage of a fractional track width landed on
      // sub-pixels at the seam and read as a tiny jerk every cycle.
      const measure = () => {
        const cs = getComputedStyle(this);
        const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
        const w = this.track.getBoundingClientRect().width;
        if (w > 0) this.style.setProperty('--vr-marquee-distance', Math.round(w + gap) + 'px');
        // Round 25: as many copies of the track as the window needs. All copies slide by one track width per
        // cycle, so at the end of a cycle the strip covers (copies - 1) track widths; with two copies a window
        // wider than one track showed a bare stretch at its right until the loop restarted.
        if (w > 0) {
          const need = Math.ceil(this.getBoundingClientRect().width / (w + gap)) + 1;
          while (this.querySelectorAll('.vr-carousel__track').length < need) {
            const more = this.track.cloneNode(true);
            more.setAttribute('aria-hidden', 'true');
            more.querySelectorAll('a, button, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
            this.appendChild(more);
          }
        }
      };
      measure();
      if (document.readyState !== 'complete') window.addEventListener('load', measure, { once: true });
      window.addEventListener('resize', this.debounce(measure, 200));
    }

    debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), ms); }; }
  }

  customElements.define('vr-carousel', VrCarousel);
  /*
    <vr-rotator> — the announcement bar and the press strip. Messages are stacked in one grid cell and roll
    upward (0.65 s, see vr-carousel.css) instead of the old sideways scroll-snap that made the top of the
    page look jittery. Pauses while hovered or focused and when the tab is hidden; with data-hover-hold the
    pointer only holds a message for that many milliseconds. The theme editor selects a block to preview it.
  */
  class VrRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.vr-rotator__item'));
      if (this.items.length < 2) { this.classList.add('vr-rotator--static'); return; }
      this.index = Math.max(0, this.items.findIndex(el => el.classList.contains('is-active')));
      this.interval = Math.max(2500, parseInt(this.dataset.interval || '5000', 10));
      this.autoplay = this.dataset.autoplay !== 'false';
      this.paused = false;
      this.timer = null;
      this.items.forEach((el, i) => this.setState(el, i === this.index));

      const prev = this.querySelector('.vr-rotator__prev');
      const next = this.querySelector('.vr-rotator__next');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      // data-hover-hold (ms): the pointer holds the current message that long, then the messages move on even
      // if it still rests there (the press strip). Without it a hovered rotator stays paused (the
      // announcement bar). Keyboard focus always pauses.
      const hold = parseInt(this.dataset.hoverHold || '0', 10);
      ['pointerenter', 'focusin'].forEach(ev => this.addEventListener(ev, () => {
        this.paused = true;
        clearTimeout(this.holdTimer);
        if (hold > 0 && ev === 'pointerenter') this.holdTimer = setTimeout(() => { if (!this.matches(':focus-within')) this.paused = false; }, hold);
      }));
      ['pointerleave', 'focusout'].forEach(ev => this.addEventListener(ev, () => { clearTimeout(this.holdTimer); this.paused = false; }));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', e => { const i = this.items.indexOf(e.target); if (i > -1) { this.show(i, 1); this.stop(); } });
        document.addEventListener('shopify:block:deselect', e => { if (this.items.includes(e.target)) this.start(); });
      }
      this.start();
    }

    disconnectedCallback() { this.stop(); }

    setState(el, on) {
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
      el.querySelectorAll('a, button').forEach(a => { a.tabIndex = on ? 0 : -1; });
    }

    show(i, direction) {
      const n = this.items.length;
      i = (i + n) % n;
      if (i === this.index) return;
      const leaving = this.items[this.index];
      const entering = this.items[i];
      // Direction sets where the entering message waits (below for next, above for previous); the forced
      // reflow commits that start position before the transition begins.
      this.classList.toggle('vr-rotator--reverse', direction < 0);
      entering.classList.remove('is-leaving');
      void entering.offsetHeight;
      this.setState(leaving, false);
      leaving.classList.add('is-leaving');
      clearTimeout(leaving.vrLeaveTimer);
      leaving.vrLeaveTimer = setTimeout(() => leaving.classList.remove('is-leaving'), 750);
      this.index = i;
      this.setState(entering, true);
    }

    step(delta) { this.show(this.index + delta, delta); this.start(); /* a manual change earns a full interval */ }

    start() {
      this.stop();
      if (!this.autoplay || reducedMotion.matches) return;
      this.timer = setInterval(() => { if (!this.paused) this.show(this.index + 1, 1); }, this.interval);
    }

    stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
  }

  customElements.define('vr-rotator', VrRotator);

  /*
    <vr-index> — the collections index (sections/collections-index.liquid). Names on one side, a tall image
    panel on the other with a fixed caption (the collection's line + Explore). Resting on a name, or the
    timer, cross-fades its image and caption in. Nothing changes height, so the page never shifts; on phones
    the row of names scrolls itself (never the window), a first tap switches and a second opens.
    Slides after the first two carry data-vr-index-src / -srcset (not data-vr-src, which hydrateDeferred would
    release all at once): a slide is given its real attributes when it is about to be shown, and the one after it
    at the same time, so the image is downloading before its fade begins.
  */
  class VrIndex extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.vr-index__item'));
      this.slides = Array.from(this.querySelectorAll('.vr-index__slide'));
      this.tags = Array.from(this.querySelectorAll('.vr-index__tag'));
      // Round 25: the wordmarks (on the photograph on desktop, below it on phones) and the two chevrons
      this.marks = Array.from(this.querySelectorAll('.vr-index__mark, .vr-index__mark-link'));
      this.list = this.querySelector('.vr-index__list');
      if (this.items.length < 2) return;
      this.cta = this.querySelector('.vr-index__cta');
      this.ctaName = this.querySelector('.vr-index__cta-name');
      this.index = Math.max(0, this.items.findIndex(el => el.classList.contains('is-active')));
      this.interval = parseInt(this.dataset.autoplay || '0', 10);
      this.touch = window.matchMedia('(hover: none)').matches;
      this.paused = false;
      this.visible = true;
      this.items.forEach((el, i) => {
        el.addEventListener('mouseenter', () => { if (!this.touch) this.show(i); });
        el.addEventListener('focus', () => { if (!this.touch) this.show(i); }); // round 24: a tap focuses the link before it clicks it; on a touch screen the click decides
        el.addEventListener('click', e => {
          if (this.touch && i !== this.index) { e.preventDefault(); this.show(i); this.restart(); }
        });
      });
      // Phones: a sideways swipe on the photograph moves to the next or the previous collection (the row of names
      // below it scrolls on its own; the photograph itself used to answer nothing to a swipe).
      this.querySelectorAll('.vr-index__step').forEach(btn => btn.addEventListener('click', () => {
        const n = this.items.length;
        this.show((this.index + (btn.classList.contains('vr-index__step--next') ? 1 : n - 1)) % n);
        this.restart();
      }));
      this.measureMarks();
      const stage = this.querySelector('.vr-index__stage');
      if (stage) {
        let sx = 0, sy = 0, live = false;
        stage.addEventListener('touchstart', e => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; live = e.touches.length === 1; }, { passive: true });
        stage.addEventListener('touchend', e => {
          if (!live) return;
          live = false;
          const t = e.changedTouches[0];
          const dx = t.clientX - sx, dy = t.clientY - sy;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
          const n = this.items.length;
          this.show((this.index + (dx < 0 ? 1 : n - 1)) % n);
          this.restart();
        }, { passive: true });
        stage.addEventListener('touchcancel', () => { live = false; }, { passive: true });
      }
      // Round 24: the row of names is a dial. Whichever name comes to rest at the centre after a flick is the
      // collection shown; nothing is decided while a finger is still on the row.
      if (this.list) {
        const list = this.list;
        let settle = 0, held = false;
        const pick = () => {
          if (held) return;
          const c = list.scrollLeft + list.clientWidth / 2;
          let best = this.index, gap = Infinity;
          this.items.forEach((el, i) => { const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - c); if (d < gap) { gap = d; best = i; } });
          if (best !== this.index) { this.show(best); this.restart(); }
        };
        const queue = () => { clearTimeout(settle); settle = setTimeout(pick, 160); };
        list.addEventListener('scroll', queue, { passive: true });
        list.addEventListener('touchstart', () => { held = true; }, { passive: true });
        ['touchend', 'touchcancel'].forEach(ev => list.addEventListener(ev, () => { held = false; queue(); }, { passive: true }));
        this.centre(true);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.centre(true));
        let width = window.innerWidth;
        window.addEventListener('resize', () => { if (window.innerWidth !== width) { width = window.innerWidth; this.centre(true); } }, { passive: true });
      }      ['pointerenter', 'focusin', 'touchstart'].forEach(ev => this.addEventListener(ev, () => { this.paused = true; }, { passive: true }));
      ['pointerleave', 'focusout', 'touchend', 'touchcancel'].forEach(ev => this.addEventListener(ev, () => { this.paused = false; }, { passive: true }));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.restart()));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; }, { threshold: 0.2 }).observe(this);
      }
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', e => {
          const link = e.target && e.target.querySelector ? e.target.querySelector('.vr-index__item') : null;
          const i = this.items.indexOf(link);
          if (i > -1) { this.show(i); this.stop(); }
        });
        document.addEventListener('shopify:block:deselect', () => this.restart());
      }
      this.hydrate((this.index + 1) % this.slides.length);
      this.restart();
    }

    disconnectedCallback() { this.stop(); }

    hydrate(i) {
      const slide = this.slides[i];
      if (!slide) return;
      slide.querySelectorAll('img[data-vr-index-src], img[data-vr-index-srcset]').forEach(el => {
        if (el.dataset.vrIndexSrcset) { el.setAttribute('srcset', el.dataset.vrIndexSrcset); el.removeAttribute('data-vr-index-srcset'); }
        if (el.dataset.vrIndexSrc) { el.setAttribute('src', el.dataset.vrIndexSrc); el.removeAttribute('data-vr-index-src'); }
      });
    }

    show(i) {
      if (i === this.index) return;
      this.hydrate(i);
      this.hydrate((i + 1) % this.slides.length);
      const set = (arr, idx, on) => {
        const el = arr[idx]; if (!el) return;
        el.classList.toggle('is-active', on);
        if (el.classList.contains('vr-index__slide')) el.setAttribute('aria-hidden', on ? 'false' : 'true');
      };
      set(this.items, this.index, false); set(this.slides, this.index, false); set(this.tags, this.index, false);
      this.index = i;
      set(this.items, i, true); set(this.slides, i, true); set(this.tags, i, true);
      this.marks.forEach(m => { const on = Number(m.dataset.vrIndex) === i; m.classList.toggle('is-active', on); if (m.hasAttribute('href')) m.tabIndex = on ? 0 : -1; });
      const item = this.items[i];
      if (this.cta) { this.cta.href = item.href; if (this.ctaName) this.ctaName.textContent = item.dataset.name || ''; }
      this.centre(false);
    }

    // Round 25: the extent of the ink in each wordmark file, measured once on a small canvas (the files are white
    // on transparent and the CDN allows it). The box of the mark takes that extent's aspect ratio and shows the
    // file as a mask of that part, so marks drawn with very different margins stand at one height. A file that
    // cannot be read stays as it was drawn, fitted whole.
    measureMarks() {
      const boxes = Array.from(this.querySelectorAll('.vr-index__mark-box[data-vr-mark]'));
      if (!boxes.length) return;
      const byUrl = new Map();
      boxes.forEach(b => { const u = b.dataset.vrMark; if (!byUrl.has(u)) byUrl.set(u, []); byUrl.get(u).push(b); });
      const run = () => byUrl.forEach((list, url) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => {
          try {
            const W = 240, H = Math.max(1, Math.round(W * im.naturalHeight / im.naturalWidth));
            const c = document.createElement('canvas'); c.width = W; c.height = H;
            const g = c.getContext('2d'); g.drawImage(im, 0, 0, W, H);
            const d = g.getImageData(0, 0, W, H).data;
            let x0 = W, y0 = H, x1 = -1, y1 = -1;
            for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) { if (d[(y * W + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } }
            if (x1 < 0) return;
            const fw = (x1 + 1 - x0) / W, fh = (y1 + 1 - y0) / H, fx = x0 / W, fy = y0 / H;
            const ar = (fw * im.naturalWidth) / (fh * im.naturalHeight);
            const px = fw >= 0.999 ? 50 : fx / (1 - fw) * 100, py = fh >= 0.999 ? 50 : fy / (1 - fh) * 100;
            list.forEach(b => {
              b.style.setProperty('--ink-ar', ar.toFixed(4));
              b.style.setProperty('--ink-boost', Math.min(1.42, Math.max(0.78, Math.sqrt(1.7 / ar))).toFixed(3)); // the shape has a say in the height
              b.style.setProperty('--ink-size', (100 / fw).toFixed(2) + '% ' + (100 / fh).toFixed(2) + '%');
              b.style.setProperty('--ink-pos', px.toFixed(2) + '% ' + py.toFixed(2) + '%');
              b.classList.add('is-measured');
            });
          } catch (e) { /* a file the canvas may not read: shown whole, as drawn */ }
        };
        im.src = url;
      });
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2500 }); else setTimeout(run, 600);
    }

    // Phones: the active name stands at the centre of the row; the row scrolls, never the document. Half a row of
    // space at each end (in the CSS of the section) lets the first and the last name reach the centre too.
    centre(instant) {
      const list = this.list, item = this.items[this.index];
      if (!list || !item || list.scrollWidth <= list.clientWidth + 4) return;
      const left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
    }

    restart() {
      this.stop();
      if (this.interval > 0 && !reducedMotion.matches) {
        this.timer = setInterval(() => { if (!this.paused && this.visible) this.show((this.index + 1) % this.items.length); }, this.interval);
      }
    }

    stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
  }

  customElements.define('vr-index', VrIndex);

  /* ---------- <vr-reveal>: one-time entrance when a block scrolls into view ----------
     Adds is-armed at once (the block's own CSS hides its words and holds a slight zoom on its image) and
     is-inview the first time a fifth of the block is visible; the CSS animates from there. Visitors who
     prefer reduced motion, and browsers without IntersectionObserver, get the finished state directly. */
  class VrReveal extends HTMLElement {
    connectedCallback() {
      if (this.classList.contains('is-inview')) return;
      if (reducedMotion.matches || !('IntersectionObserver' in window)) { this.classList.add('is-inview'); return; }
      this.classList.add('is-armed');
      const io = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) { this.classList.add('is-inview'); io.disconnect(); }
      }, { threshold: 0.2 });
      io.observe(this);
    }
  }
  customElements.define('vr-reveal', VrReveal);
})();
